import {
	Observable, tap, filter, of,
} from 'rxjs';

import {
	Service,
	Container,
	AuthService,
	StreakService,
	ILeaderboard,
	ILeaderboardUser,
	ILevelZone,
	LeaderboardState,
	UserState,
	XpSourceType,
	DataService,
} from '../../../../symphony';

import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import { LEADERBOARD_USERS_COUNT, SlLEContext } from '../../global-constants';
import { ILeaderboardUserWithPos } from '../../global-interface';

@Service()
export class SlLeaderboardService {
	private api = Container.take(SlLEContext, SlApiService);

	private data = Container.take(SlLEContext, SlDataService);

	private globalData = Container.take('global', DataService);

	private authService = Container.take('global', AuthService);

	private streakService = Container.take('global', StreakService);

	public getLeaderboardPredictedState = (): Observable<ILeaderboard> => {
		const predictedXPsForLessonComplete = this.globalData.xpSourcesList$.value?.xpSources.find(
			(xp) => xp.xpSource === XpSourceType.LessonComplete,
		).xp || 0;
		const predictedXPsFromStreak = this.streakService.getPredictedXPs() || 0;
		const predictedXPs = predictedXPsForLessonComplete + predictedXPsFromStreak;
		return this.api.getLeaderboardPredictedState(predictedXPs).pipe(
			filter((d) => !!d),
			tap(this.setupLeaderboardData),
		);
	};

	public cleanupLeaderboardData = (): Observable<null> => {
		this.data.isLeaderboardAvailable = false;
		this.data.leaderboardStates = null;
		this.data.leagueRank = null;

		return of(null);
	};

	private setupLeaderboardData = (leaderboard: ILeaderboard): void => {
		this.data.leagueRank = leaderboard.leagueRank;
		this.setLeaderboardUsers(leaderboard);
		this.setIsLeaderboardAvailable(leaderboard);
	};

	private getLeaderboardUsersStartIndex = (
		leaderboardUsers: ILeaderboardUser[],
	): number => {
		const userIndex = this.findUserIndex(leaderboardUsers);
		const gap = Math.floor(LEADERBOARD_USERS_COUNT / 2);
		if (userIndex < gap) {
			return 0;
		}
		if (leaderboardUsers.length - userIndex < gap) {
			return leaderboardUsers.length - LEADERBOARD_USERS_COUNT;
		}
		return userIndex - gap;
	};

	private generateUsersWithPosition(
		leaderboardUsers: ILeaderboardUser[],
	): ILeaderboardUserWithPos[] {
		return leaderboardUsers.map((user, index) => ({ ...user, position: index + 1 }));
	}

	private setLeaderboardUsers = (leaderboard: ILeaderboard): void => {
		const {
			leaderboardUsers,
			config: { levelDownIndex, levelUpIndex },
		} = leaderboard;

		const prevLeaderboardUsers = this.getPrevUsers(leaderboardUsers);

		const { current, predicted } = this.getUsersWithZones(
			this.generateUsersWithPosition(prevLeaderboardUsers),
			this.generateUsersWithPosition(leaderboardUsers),
			levelUpIndex,
			levelDownIndex,
		);

		const predStartIndex = this.getLeaderboardUsersStartIndex(leaderboardUsers);
		const currStartIndex = this.getLeaderboardUsersStartIndex(prevLeaderboardUsers);

		const currentState = current.splice(
			currStartIndex,
			LEADERBOARD_USERS_COUNT,
		);

		const predictedState = predicted.splice(
			predStartIndex,
			LEADERBOARD_USERS_COUNT,
		);

		this.data.leaderboardStates = {
			currentState,
			predictedState,
		};
	};

	private getPrevUsers = (
		predictedUsers: ILeaderboardUser[],
	): ILeaderboardUser[] => {
		const authUserId = this.authService?.getUser()?.id;
		const prevLeaderboardUsers: ILeaderboardUser[] = [];

		let userPicked = false;
		let user: ILeaderboardUser = null;
		let userPushed = false;

		for (let i = 0; i <= predictedUsers.length; ++i) {
			const item = predictedUsers[i];
			if (!userPicked) {
				// User found
				if (item?.userId === authUserId) {
					user = item;
					userPicked = true;
					if (i + 1 === user.previousPosition) {
						return predictedUsers;
					}
				} else {
					// Before finding user
					prevLeaderboardUsers.push(item);
				}
			// User picked
			} else if (!userPushed) {
				// Middle
				prevLeaderboardUsers.push(item);
				if (i + 1 === user.previousPosition) {
					// My previous position
					prevLeaderboardUsers.push(user);
					userPushed = true;
				}
			} else {
				// User pushed
				prevLeaderboardUsers.push(item);
			}
		}

		return prevLeaderboardUsers;
	};

	private getUsersWithZones = (
		currentUsers: ILeaderboardUserWithPos[],
		predictedUsers: ILeaderboardUserWithPos[],
		levelUpIndex: number,
		levelDownIndex: number,
	): {
			current: (ILeaderboardUserWithPos | ILevelZone)[];
			predicted: (ILeaderboardUserWithPos | ILevelZone)[];
		} => {
		const current = [...currentUsers] as (
			| ILeaderboardUserWithPos
			| ILevelZone
		)[];
		const predicted = [...predictedUsers] as (
			| ILeaderboardUserWithPos
			| ILevelZone
		)[];

		if (levelUpIndex >= currentUsers.length) {
			predicted.push({
				isLevelUpZone: true,
			});
			current.push({
				isLevelUpZone: true,
			});
		} else {
			if (levelDownIndex > currentUsers.length) {
				predicted.push({
					isLevelDownZone: true,
				});
				current.push({
					isLevelDownZone: true,
				});
			} else {
				predicted.splice(levelDownIndex, 0, {
					isLevelDownZone: true,
				});
				current.splice(levelDownIndex, 0, {
					isLevelDownZone: true,
				});
			}
			predicted.splice(levelUpIndex, 0, {
				isLevelUpZone: true,
			});
			current.splice(levelUpIndex, 0, {
				isLevelUpZone: true,
			});
		}

		return {
			current,
			predicted,
		};
	};

	private setIsLeaderboardAvailable = (
		leaderboardData: ILeaderboard,
	): void => {
		const user = this.findUser(leaderboardData);
		const actualPosition = this.findUserIndex(leaderboardData.leaderboardUsers) + 1;
		const leaderboardIsEnabled = (user?.userConfig?.state === UserState.Ok
                && leaderboardData.state === LeaderboardState.StartedAndOpen)
            || leaderboardData.state === LeaderboardState.StartedAndClosed;

		this.data.isLeaderboardAvailable = leaderboardIsEnabled
            && (user?.previousPosition > actualPosition
                || this.data.lessonOrderInCourse$.value === 2);
	};

	private findUser = (leaderboardData: ILeaderboard): ILeaderboardUser => {
		const authUserId = this.authService?.getUser()?.id;
		return leaderboardData?.leaderboardUsers?.find(
			(user) => user.userId === authUserId,
		);
	};

	private findUserIndex = (leaderboardUsers: ILeaderboardUser[]): number => {
		const authUserId = this.authService?.getUser()?.id;
		return leaderboardUsers?.findIndex(
			(user) => user?.userId === authUserId,
		);
	};
}
